/**
 * COMPAT: This entire file can be deleted when web/client is deprecated.
 *
 * Adapter that normalises Worker backend messages to the shape the legacy
 * client expects. The Worker wire format (shared-types) differs from the Go
 * backend format in a few places; this adapter bridges the gap.
 *
 * Patched incompatibilities:
 *   1. story.captain: Worker sends flat PlayerSummary `{ id, name, sprite_id }`,
 *      Go sends full Player with `meta.sprite_id` wrapper.
 *   2. vote.award: Worker sends flat AwardMeta `{ name, rule, ... }`,
 *      Go sends nested `{ id, meta: { name, rule, ... } }`.
 *   3. submission.player: Worker may omit `.player` on submissions,
 *      Go always includes it. (Handled via optional chaining in components.)
 */

import type { GameMessage, Story, StoryVote } from "./types";

/** Worker's PlayerSummary shape (flat sprite_id, no `meta` wrapper). */
interface WorkerPlayerSummary {
  id: number;
  name: string;
  sprite_id?: number;
  meta?: { sprite_id?: number };
}

/** Worker's flat award shape on votes (no `meta` wrapper). */
interface WorkerVoteAward {
  name?: string;
  subtitle?: string;
  rule?: number;
  points?: number;
  meta?: { name: string; subtitle: string; rule: number; points: number };
}

/**
 * If captain lacks the `meta` wrapper the legacy UI expects, synthesise it
 * from the flat `sprite_id` field.
 */
function patchCaptain(captain: WorkerPlayerSummary): void {
  if (captain && !captain.meta) {
    captain.meta = { sprite_id: captain.sprite_id ?? 0 };
  }
}

/**
 * Worker sends vote.award as flat AwardMeta { name, rule, ... }.
 * Legacy client expects vote.award = { id, meta: { name, rule, ... } }.
 */
function patchVote(vote: StoryVote): void {
  if (!vote.award) return;
  const award = vote.award as unknown as WorkerVoteAward;
  // Already patched or already in Go format
  if (award.meta) return;
  // Wrap flat fields into nested { meta: { ... } } shape
  vote.award = {
    id: (vote as unknown as { award_id?: number }).award_id ?? 0,
    meta: {
      name: award.name ?? "",
      subtitle: award.subtitle ?? "",
      rule: award.rule ?? 0,
      points: award.points ?? 0,
    },
  };
}

function patchStory(story: Story): void {
  if (story.captain) {
    patchCaptain(story.captain as unknown as WorkerPlayerSummary);
  }
  if (story.submissions) {
    for (const sub of story.submissions) {
      if (sub.player) {
        patchCaptain(sub.player as unknown as WorkerPlayerSummary);
      }
    }
  }
}

/**
 * Normalise a GameMessage from the Worker backend so it matches the shape
 * the legacy client expects. Mutates in place for efficiency.
 */
export function adaptWorkerMessage(msg: GameMessage): GameMessage {
  if (msg.game?.stories) {
    for (const story of msg.game.stories) {
      patchStory(story);
    }
  }

  if (msg.player?.current_story) {
    patchStory(msg.player.current_story);
  }

  // Patch vote awards
  if (msg.player?.votes) {
    for (const vote of msg.player.votes) {
      patchVote(vote);
    }
  }

  return msg;
}
